import { dayjs } from '../../dayjs.ts'
// Vendored from @vvo/tzdb's raw-time-zones.json (MIT license, https://github.com/vvo/tzdb).
// Static metadata only (no baked-in offsets, which go stale) - offsets/current time are computed
// live via dayjs below. Re-copy this file from a newer @vvo/tzdb release to pick up IANA tzdata updates.
import rawTimeZones from './raw-time-zones.json' with { type: 'json' }

export function getSystemTimezone(): string {
	return dayjs.tz.guess()
}

type RawTimezone = (typeof rawTimeZones)[number]

export type Timezone = {
	value: string
	/** Canonical "Region/City" form, e.g. "Europe/Helsinki" - the IANA zone id with '_' as ' '. Used
	 * both outside the picker (reports etc) and as the picker's own display name. */
	label: string
	mainCity: string
	mainCities: string[]
	alternativeName: string
	countryName: string
	continentName: string
}

const toTimezone = (tz: RawTimezone): Timezone => ({
	value: tz.name,
	label: tz.name.replaceAll('_', ' '),
	mainCity: tz.mainCities[0] ?? tz.name,
	mainCities: tz.mainCities,
	alternativeName: tz.alternativeName,
	countryName: tz.countryName,
	continentName: tz.continentName,
})

// @vvo/tzdb does not include a plain "UTC" zone - add it manually since it's a common default
// (e.g. schema.tsx and useFormatDate.tsx both fall back to 'UTC') and must resolve in the picker.
export const UTC_TIMEZONE: Timezone = {
	value: 'UTC',
	label: 'UTC',
	mainCity: 'UTC',
	mainCities: ['UTC'],
	alternativeName: 'Coordinated Universal Time GMT Greenwich',
	countryName: '',
	continentName: '',
}

export const timezones: Timezone[] = [UTC_TIMEZONE, ...rawTimeZones.map(toTimezone)].sort((a, b) => a.label.localeCompare(b.label))

// Some IANA names are aliases folded into a canonical zone (e.g. Europe/Mariehamn -> Europe/Helsinki).
// Keep a lookup so previously-saved preference values still resolve to their canonical entry.
const timezoneByValue = new Map(timezones.map((tz) => [tz.value, tz]))
for (const raw of rawTimeZones) {
	const canonical = timezoneByValue.get(raw.name)
	if (!canonical) continue
	for (const alias of raw.group) {
		if (!timezoneByValue.has(alias)) timezoneByValue.set(alias, canonical)
	}
}

export const findTimezone = (value: string) => timezoneByValue.get(value)

export const getTimezoneLabel = (timezone: string) => findTimezone(timezone)?.label ?? timezone

export const getTimezoneOffset = (timezone: string) => dayjs().tz(timezone).format('Z')
